/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, {useState, useEffect} from 'react';
import {useMediaQuery} from 'react-responsive';
import {
  Button,
  Fab,
  CircularProgress,
  TextField,
  IconButton,
  InputAdornment,
  Divider,
  Drawer,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MenuIcon from '@mui/icons-material/Menu';
import {Visibility, VisibilityOff} from '@mui/icons-material';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
  signOut,
} from 'firebase/auth';
import styled, {keyframes} from 'styled-components';
import {Link, useSearchParams} from 'react-router-dom';
import Tab from '../../Components/tab';
import Footer from '../../Components/Footer';
import logo from '../../Assets/logo.png';

/**
 * Cadastro leve pro módulo Finanças (entreprise-ireit) — usado por dois
 * caminhos: (1) quem foi convidado pra um grupo financeiro existente
 * (?token=... na URL, ver `conviteToken`) e (2) quem chega pela landing
 * pública (`/financas`, sem convite nenhum) e quer começar do zero. Em
 * ambos os casos a pessoa ainda não tem conta na Irê it — sem isso, nem
 * consegue logar em myireit.com.br (routes.js lá exige um documento em
 * employers_modules pra qualquer sessão autenticada, senão desloga na
 * hora). Espelha register_gestor/index.jsx nos padrões visuais e no fluxo
 * de auth, mas SEM escolha de plano/zona — só cria o documento em
 * employers_modules (POST /employers/insert) direto.
 *
 * Caso (1): depois do cadastro, vincula ao grupo financeiro que mandou o
 * convite (POST /financas/convites/aceitar[-token]). Caso (2): esse aceite
 * de convite é um no-op silencioso (não existe convite pra aceitar) — a
 * pessoa cai em myireit.com.br sem grupo nenhum, e `Financas/index.js`
 * (`CriarGrupoView`/`criarGrupo`, `POST /grupos`) já mostra a tela de criar
 * um grupo do zero automaticamente. Não precisa de nenhum fluxo extra aqui
 * pra isso — só a copy da tela (`STitle sub=`) muda conforme tem token ou
 * não, pra não dizer "grupo que te convidou" pra quem nunca foi convidado.
 */

const API_BASE = 'https://kops.apiireit.com/gateway';

const slideUp = keyframes`
  from { transform: translateY(32px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;
const SlideUp = styled.div`
  animation: ${slideUp} 0.55s ease-out both;
  animation-delay: ${p => p.delay || '0s'};
`;

const C = {
  azul: '#06448e',
  azulD: '#04316a',
  laranja: '#FF6600',
  laranjaD: '#cc4400',
  roxo: '#7c53e3',
  bg: '#f4f6fa',
  text: '#1a1a2e',
  muted: '#8a94a6',
  white: '#ffffff',
  success: '#059669',
};

const Card = ({children, sm, maxWidth = 520}) => (
  <div
    style={{
      background: C.white,
      borderRadius: sm ? 16 : 24,
      padding: sm ? '24px 16px' : '36px 32px',
      maxWidth,
      width: '100%',
      margin: '0 auto',
      boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
      boxSizing: 'border-box',
    }}
  >
    {children}
  </div>
);

const STitle = ({children, sub}) => (
  <div style={{marginBottom: 24}}>
    <p style={{margin: 0, fontSize: 20, fontWeight: 800, color: C.text}}>
      {children}
    </p>
    {sub && (
      <p style={{margin: '4px 0 0', fontSize: 14, color: C.muted}}>{sub}</p>
    )}
  </div>
);

const RegisterFinancas = () => {
  const auth = getAuth();
  const sm = useMediaQuery({query: '(max-width: 750.98px)'});
  const vsm = useMediaQuery({query: '(max-width: 450px)'});
  const [searchParams] = useSearchParams();

  const [toggleBar, setToggleBar] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [step, setStep] = useState('auth');
  const [authUser, setAuthUser] = useState(null);

  // ── auth ──────────────────────────────────────────────────────────────
  const [authMode, setAuthMode] = useState('register');
  const [authName, setAuthName] = useState('');
  const [authEmail, setAuthEmail] = useState(searchParams.get('email') || '');
  // pedido explícito do usuário: convite agora carrega um token único
  // (GrupoFinanceiroServices.convidar, expira em 30min) — amarra o aceite
  // a ESSE convite específico (aceitarConvitePorToken) em vez do sweep
  // genérico por e-mail de antes (aceitarConvites, mantido como
  // fallback pra quem chega aqui sem token, ex: link antigo)
  const conviteToken = searchParams.get('token') || '';
  const [authPass, setAuthPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  // ── cadastro ──────────────────────────────────────────────────────────
  // pedido explícito do usuário: cadastro pedia CPF/RG/CEP/endereço/dados
  // bancários — tudo isso é exigido pelo employers_modules (documento
  // pra funcionário/gestor de verdade), mas o módulo Finanças nunca usa
  // nada disso. Só nome (exibição) e telefone (opcional, lembrete por
  // WhatsApp) são reais aqui — os campos que o backend ainda exige mas
  // não importam pra Finanças vão como string vazia (ver handleSubmit).
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cadastroError, setCadastroError] = useState('');

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo({top: 0, behavior: 'smooth'});
    signOut(auth).catch(() => {});
  }, []);

  useEffect(() => {
    const t = () => setIsVisible(window.pageYOffset > 50);
    window.addEventListener('scroll', t);
    return () => window.removeEventListener('scroll', t);
  }, []);

  // ── auth handlers ─────────────────────────────────────────────────────
  const handleEmailAuth = async () => {
    setAuthError('');
    if (!authEmail.trim()) return setAuthError('Email obrigatório.');
    if (!authPass) return setAuthError('Senha obrigatória.');
    if (authMode === 'register' && !authName.trim())
      return setAuthError('Informe seu nome.');
    if (authMode === 'register' && authPass.length < 6)
      return setAuthError('Senha: mínimo 6 caracteres.');
    setAuthLoading(true);
    try {
      let cred;
      if (authMode === 'register') {
        cred = await createUserWithEmailAndPassword(
          auth,
          authEmail.trim(),
          authPass,
        );
        await updateProfile(cred.user, {displayName: authName.trim()});
      } else {
        cred = await signInWithEmailAndPassword(
          auth,
          authEmail.trim(),
          authPass,
        );
      }
      setAuthUser(cred.user);
      setNome(cred.user.displayName || authName);
      setStep('cadastro');
    } catch (err) {
      const map = {
        'auth/email-already-in-use': 'Email já cadastrado. Faça login.',
        'auth/wrong-password': 'Senha incorreta.',
        'auth/user-not-found': 'Usuário não encontrado.',
        'auth/invalid-credential': 'Email ou senha incorretos.',
        'auth/too-many-requests': 'Muitas tentativas. Tente mais tarde.',
      };
      setAuthError(map[err.code] || 'Erro ao autenticar.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleGoogle = async () => {
    setAuthError('');
    setAuthLoading(true);
    try {
      const res = await signInWithPopup(auth, new GoogleAuthProvider());
      setAuthUser(res.user);
      setNome(res.user.displayName || '');
      setStep('cadastro');
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user')
        setAuthError('Erro ao entrar com Google.');
    } finally {
      setAuthLoading(false);
    }
  };

  const validateCadastro = () => {
    if (!nome.trim())
      return setCadastroError('Informe seu nome completo.'), false;
    setCadastroError('');
    return true;
  };

  // ── submit ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!authUser || !validateCadastro()) return;
    setSubmitting(true);
    try {
      const token = await authUser.getIdToken();
      const payload = {
        epid: authUser.uid,
        cdsid: authUser.uid,
        // Único fluxo de auto-cadastro que já sabe, sem ambiguidade, que o
        // usuário é só-Finanças (register_gestor é o cadastro de zona de
        // negócio e não seta cargo — é atribuído por fora) — entreprise-ireit
        // usa isso pra redirecionar direto pra Finanças e trocar a sidebar.
        // Valor técnico de propósito (não "Financeiro") — esse campo é
        // texto livre em qualquer cadastro de funcionário real, e um
        // funcionário cujo cargo por acaso fosse "Financeiro" cairia,
        // sem essa mudança, direto na tela errada.
        cargo: 'financeiro_user',
        nome: nome.trim(),
        // employers_modules exige esses campos (@NotNull em
        // Employersmodels.java) pra QUALQUER documento — mas são coisa de
        // funcionário/gestor real (CPF, RG, endereço, dados bancários),
        // sem uso nenhum pro módulo Finanças. Manda vazio de propósito em
        // vez de pedir pra quem só quer entrar num grupo financeiro —
        // @NotNull aceita string vazia, só não aceita null.
        cpf: '',
        rg: '',
        end: '',
        num: 0,
        bairro: '',
        cid: '',
        estado: '',
        pais: 'Brasil',
        Banco: '',
        Agencia: '',
        Conta: '',
        TConta: '',
        pix: '',
      };

      const res = await fetch(`${API_BASE}/employers/insert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const criado = await res.json();
      if (criado?.map) {
        // addDocument devolve doc.map preenchido em vez de status HTTP de
        // erro quando a validação falha — mesmo padrão de outros *insert
        // desse backend (ver EmployersControler.addDocument)
        throw new Error(criado.map.message || 'Falha na validação.');
      }

      // Vincula ao grupo financeiro que mandou o convite (se houver) — best
      // effort, não bloqueia o cadastro se isso falhar (a pessoa ainda
      // consegue logar e o vínculo é tentado de novo automaticamente na
      // primeira vez que abrir a aba Finanças, via o sweep genérico
      // /convites/aceitar). Com token na URL, usa o aceite preciso — um
      // convite não pode virar vínculo com mais de uma pessoa.
      try {
        const aceitarUrl = conviteToken
          ? `${API_BASE}/financas/convites/aceitar-token?token=${encodeURIComponent(
              conviteToken,
            )}&userId=${authUser.uid}&email=${encodeURIComponent(
              authUser.email,
            )}&nome=${encodeURIComponent(
              nome.trim(),
            )}&telefone=${encodeURIComponent(telefone)}`
          : `${API_BASE}/financas/convites/aceitar?userId=${
              authUser.uid
            }&email=${encodeURIComponent(
              authUser.email,
            )}&telefone=${encodeURIComponent(telefone)}`;
        await fetch(aceitarUrl, {
          method: 'POST',
          headers: {Authorization: `Bearer ${token}`},
        });
      } catch {
        // silencioso de propósito — ver comentário acima
      }

      setStep('done');
    } catch (err) {
      setCadastroError('Erro ao enviar. Verifique os dados e tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── render: auth ──────────────────────────────────────────────────────
  const renderAuth = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <div
          style={{
            display: 'flex',
            borderRadius: 12,
            background: '#f4f6fa',
            padding: 4,
            marginBottom: 24,
          }}
        >
          {['register', 'login'].map(m => (
            <button
              key={m}
              onClick={() => {
                setAuthMode(m);
                setAuthError('');
              }}
              style={{
                flex: 1,
                padding: '9px 0',
                border: 'none',
                borderRadius: 9,
                background: authMode === m ? '#fff' : 'transparent',
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 14,
                color: authMode === m ? C.azul : C.muted,
                boxShadow:
                  authMode === m ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.2s',
                fontFamily: "'Outfit','Segoe UI',sans-serif",
              }}
            >
              {m === 'register' ? 'Criar conta' : 'Já tenho conta'}
            </button>
          ))}
        </div>

        <STitle
          sub={
            conviteToken
              ? 'Pra entrar no grupo financeiro que te convidou.'
              : 'Pra começar a organizar as finanças da sua família com o Fiinc.'
          }
        >
          {authMode === 'register' ? 'Crie sua conta' : 'Entre na sua conta'}
        </STitle>

        {authMode === 'register' && (
          <TextField
            label="Seu nome completo"
            variant="outlined"
            size="small"
            fullWidth
            style={{marginBottom: 14}}
            value={authName}
            disabled={authLoading}
            onChange={e => setAuthName(e.target.value)}
            autoComplete="name"
          />
        )}

        <TextField
          label="Email"
          type="email"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 14}}
          value={authEmail}
          disabled={authLoading}
          onChange={e => setAuthEmail(e.target.value)}
          autoComplete="email"
        />

        <TextField
          label="Senha"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 8}}
          type={showPass ? 'text' : 'password'}
          value={authPass}
          disabled={authLoading}
          onChange={e => setAuthPass(e.target.value)}
          autoComplete={
            authMode === 'register' ? 'new-password' : 'current-password'
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPass(v => !v)}
                  edge="end"
                >
                  {showPass ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') handleEmailAuth();
          }}
        />

        {authError && (
          <p
            style={{
              color: '#e53935',
              fontSize: 13,
              textAlign: 'center',
              marginBottom: 8,
              marginTop: 4,
            }}
          >
            {authError}
          </p>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleEmailAuth}
          disabled={authLoading}
          sx={{
            bgcolor: C.azul,
            ':hover': {bgcolor: C.azulD},
            borderRadius: '12px',
            height: 50,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 16,
            mt: '8px',
          }}
        >
          {authLoading ? (
            <CircularProgress size={22} style={{color: '#fff'}} />
          ) : authMode === 'register' ? (
            'Criar conta e continuar'
          ) : (
            'Entrar e continuar'
          )}
        </Button>

        <Divider style={{margin: '18px 0'}}>
          <span style={{fontSize: 12, color: C.muted, padding: '0 8px'}}>
            ou continuar com
          </span>
        </Divider>

        <button
          onClick={handleGoogle}
          disabled={authLoading}
          style={{
            width: '100%',
            height: 48,
            borderRadius: 12,
            border: '1px solid #e0e5ef',
            background: '#fff',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 14,
            color: C.text,
          }}
        >
          Continuar com Google
        </button>
      </Card>
    </SlideUp>
  );

  // ── render: cadastro ──────────────────────────────────────────────────
  const renderCadastro = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <STitle sub="Só o essencial pra sua conta ficar válida — leva 30 segundos.">
          Complete seu cadastro
        </STitle>

        <TextField
          label="Nome completo *"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 14}}
          value={nome}
          onChange={e => setNome(e.target.value)}
        />

        <TextField
          label="Celular / WhatsApp (opcional)"
          variant="outlined"
          size="small"
          fullWidth
          style={{marginBottom: 20}}
          value={telefone}
          onChange={e => setTelefone(e.target.value)}
          placeholder="(71) 99999-9999"
        />

        {cadastroError && (
          <p
            style={{
              color: '#e53935',
              fontSize: 13,
              marginTop: 4,
              marginBottom: 0,
            }}
          >
            {cadastroError}
          </p>
        )}

        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            bgcolor: C.laranja,
            ':hover': {bgcolor: C.laranjaD},
            borderRadius: '12px',
            height: 50,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 16,
            mt: '10px',
          }}
        >
          {submitting ? (
            <CircularProgress size={22} style={{color: '#fff'}} />
          ) : (
            'Concluir cadastro'
          )}
        </Button>
      </Card>
    </SlideUp>
  );

  // ── render: done ──────────────────────────────────────────────────────
  const renderDone = () => (
    <SlideUp delay="0.05s">
      <Card sm={sm}>
        <div style={{textAlign: 'center', padding: '8px 0 24px'}}>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: '#ecfdf5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              margin: '0 auto 16px',
              border: '2px solid #05966930',
            }}
          >
            ✅
          </div>
          <p style={{margin: 0, fontSize: 20, fontWeight: 800, color: C.text}}>
            Cadastro concluído!
          </p>
          <p style={{margin: '8px 0 0', fontSize: 14, color: C.muted}}>
            Já pode entrar em myireit.com.br com o email e senha que você acabou
            de criar.
          </p>
        </div>
        <Button
          fullWidth
          href="https://myireit.com.br"
          variant="contained"
          sx={{
            bgcolor: C.azul,
            ':hover': {bgcolor: C.azulD},
            borderRadius: '12px',
            height: 48,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          Ir para o painel
        </Button>
      </Card>
    </SlideUp>
  );

  return (
    <div
      style={{
        overflowX: 'hidden',
        width: '100%',
        fontFamily: "'Outfit','Segoe UI',sans-serif",
      }}
    >
      {isVisible && (
        <Fab
          onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}
          style={{position: 'fixed', bottom: 20, left: 20, zIndex: 2}}
          size="small"
          color="primary"
        >
          <ArrowUpwardIcon />
        </Fab>
      )}

      <div
        style={{
          background: C.azul,
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          zIndex: 9999,
          top: 0,
          left: 0,
          width: '100%',
        }}
      >
        <Button
          component={Link}
          to="/"
          style={{
            width: 95,
            height: 47,
            marginLeft: 12,
            backgroundImage: `url(${logo})`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        />
        {sm || vsm ? (
          <MenuIcon
            style={{color: '#fff', marginRight: 15}}
            onClick={() => setToggleBar(true)}
          />
        ) : (
          <Tab />
        )}
      </div>
      {(sm || vsm) && (
        <Drawer
          anchor="top"
          open={toggleBar}
          onClose={() => setToggleBar(false)}
        >
          <Tab setToggleBar={setToggleBar} />
        </Drawer>
      )}

      <div
        style={{
          background: `linear-gradient(145deg, ${C.azul} 0%, ${C.azul} 35%, ${C.roxo} 75%, ${C.roxo} 100%)`,
          paddingTop: 100,
          paddingBottom: 60,
          minHeight: '100vh',
        }}
      >
        <div
          style={{maxWidth: 520, margin: '0 auto', padding: sm ? '0 16px' : 0}}
        >
          {step === 'auth' && renderAuth()}
          {step === 'cadastro' && renderCadastro()}
          {step === 'done' && renderDone()}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterFinancas;
